import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Description, ExpandMore, Search } from '@mui/icons-material';
import { format } from 'date-fns';
import { FileMetadata } from '../../types';
import { apiService } from '../../services/api';
import LibraryBrowser from '../LibraryBrowser/LibraryBrowser';
import { DocumentComments } from './DocumentComments';

export type ViewMode = 'folder' | 'date' | 'name' | 'author';

interface SharePointDocumentViewerProps {
  projectId: number;
  siteId: string;
  libraryName: string;
  viewMode?: ViewMode;
  onDocumentSelect?: (document: FileMetadata) => void;
  initialFolderPath?: string;
}

const SharePointDocumentViewer: React.FC<SharePointDocumentViewerProps> = ({
  projectId: _projectId,
  siteId,
  libraryName,
  viewMode: initialViewMode = 'folder',
  onDocumentSelect,
  initialFolderPath,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [documents, setDocuments] = useState<FileMetadata[]>([]);
  const [allDocuments, setAllDocuments] = useState<FileMetadata[]>([]); // All files from entire library
  const [_loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFolderPath, setCurrentFolderPath] = useState<string | undefined>(initialFolderPath);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<FileMetadata | null>(null);

  // Load documents when siteId, libraryName, or folder path changes (for folder view)
  useEffect(() => {
    if (siteId && libraryName && viewMode === 'folder') {
      loadDocuments();
    }
  }, [siteId, libraryName, currentFolderPath, viewMode]);

  // Load all documents recursively when switching to date/name/author views
  useEffect(() => {
    if (siteId && libraryName && (viewMode === 'date' || viewMode === 'name' || viewMode === 'author')) {
      loadAllDocuments();
    }
  }, [siteId, libraryName, viewMode]);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.listSharePointFiles(
        siteId,
        undefined, // driveId - not needed when libraryName is provided
        libraryName,
        currentFolderPath
      );
      setDocuments(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Recursively load all files from the entire library
  const loadAllDocuments = async () => {
    setLoadingAll(true);
    setError(null);
    try {
      const allFiles: FileMetadata[] = [];
      
      // Recursive function to load files from a folder and its subfolders
      const loadFolderRecursively = async (folderPath?: string): Promise<void> => {
        try {
          const response = await apiService.listSharePointFiles(
            siteId,
            undefined,
            libraryName,
            folderPath
          );
          
          // Add files from current folder
          const files = response.items.filter(item => item.isFile);
          allFiles.push(...files);
          
          // Recursively load subfolders
          const folders = response.items.filter(item => item.isFolder);
          for (const folder of folders) {
            const subFolderPath = folderPath 
              ? `${folderPath}/${folder.name}` 
              : folder.name;
            await loadFolderRecursively(subFolderPath);
          }
        } catch (err) {
          console.warn(`Error loading folder ${folderPath}:`, err);
          // Continue loading other folders even if one fails
        }
      };
      
      // Start from root
      await loadFolderRecursively();
      
      setAllDocuments(allFiles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load all documents');
      console.error('Error loading all documents:', err);
    } finally {
      setLoadingAll(false);
    }
  };

  const handleViewModeChange = (_event: React.SyntheticEvent, newValue: ViewMode) => {
    setViewMode(newValue);
  };

  const handleFileSelect = (file: FileMetadata) => {
    // Set selected document for comments display
    setSelectedDocument(file);
    
    // Open file in SharePoint using webUrl
    if (file.webUrl) {
      window.open(file.webUrl, '_blank', 'noopener,noreferrer');
    }
    // Also call the callback if provided
    if (onDocumentSelect) {
      onDocumentSelect(file);
    }
  };

  const handleFolderPathChange = (folderPath: string | undefined) => {
    setCurrentFolderPath(folderPath);
  };

  // Render folder view using LibraryBrowser
  const renderFolderView = () => {
    return (
      <LibraryBrowser
        onFileSelect={handleFileSelect}
        siteId={siteId}
        libraryName={libraryName}
        folderPath={currentFolderPath}
        onFolderPathChange={handleFolderPathChange}
      />
    );
  };

  // Filter files by search term
  const filteredFiles = useMemo(() => {
    const filesToFilter = viewMode === 'folder' ? documents.filter(item => item.isFile) : allDocuments;
    if (!searchTerm.trim()) {
      return filesToFilter;
    }
    const searchLower = searchTerm.toLowerCase();
    return filesToFilter.filter(file => 
      file.name.toLowerCase().includes(searchLower) ||
      (file.lastModifiedBy || '').toLowerCase().includes(searchLower)
    );
  }, [viewMode === 'folder' ? documents : allDocuments, searchTerm, viewMode]);

  // Render by date view
  const renderDateView = () => {
    if (loadingAll) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading all documents from library...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    const files = filteredFiles;
    
    // Group files by date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const groups: { label: string; files: FileMetadata[] }[] = [
      { label: 'Today', files: [] },
      { label: 'Yesterday', files: [] },
      { label: 'This Week', files: [] },
      { label: 'This Month', files: [] },
      { label: 'Older', files: [] },
    ];

    files.forEach(file => {
      if (!file.lastModifiedDateTime) {
        groups[4].files.push(file);
        return;
      }

      const fileDate = new Date(file.lastModifiedDateTime);
      fileDate.setHours(0, 0, 0, 0);

      if (fileDate.getTime() === today.getTime()) {
        groups[0].files.push(file);
      } else if (fileDate.getTime() === yesterday.getTime()) {
        groups[1].files.push(file);
      } else if (fileDate >= weekAgo) {
        groups[2].files.push(file);
      } else if (fileDate >= monthAgo) {
        groups[3].files.push(file);
      } else {
        groups[4].files.push(file);
      }
    });

    return (
      <Box>
        {groups.map((group, index) => {
          if (group.files.length === 0) return null;
          // Sort files by name within each group
          const sortedFiles = [...group.files].sort((a, b) => a.name.localeCompare(b.name));
          return (
            <Accordion key={index} defaultExpanded={index < 2}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">
                  {group.label} ({group.files.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {sortedFiles.map((file) => (
                    <ListItem key={file.itemId} disablePadding>
                      <ListItemButton onClick={() => handleFileSelect(file)}>
                        <ListItemIcon>
                          <Description />
                        </ListItemIcon>
                        <ListItemText 
                          primary={file.name}
                          secondary={file.lastModifiedDateTime 
                            ? format(new Date(file.lastModifiedDateTime), 'MMM dd, yyyy HH:mm')
                            : 'Date unknown'}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
        {files.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No documents found
          </Typography>
        )}
      </Box>
    );
  };

  // Render by name view
  const renderNameView = () => {
    if (loadingAll) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading all documents from library...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    const files = filteredFiles;
    const sortedFiles = [...files].sort((a, b) => a.name.localeCompare(b.name));

    // Group by first letter
    const groups: Record<string, FileMetadata[]> = {};
    sortedFiles.forEach(file => {
      const firstLetter = file.name.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(file);
    });

    return (
      <Box>
        {Object.keys(groups).sort().map(letter => (
          <Accordion key={letter} defaultExpanded={true}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">
                {letter} ({groups[letter].length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {groups[letter].map((file) => (
                  <ListItem key={file.itemId} disablePadding>
                    <ListItemButton onClick={() => handleFileSelect(file)}>
                      <ListItemIcon>
                        <Description />
                      </ListItemIcon>
                      <ListItemText 
                        primary={file.name}
                        secondary={file.lastModifiedDateTime 
                          ? format(new Date(file.lastModifiedDateTime), 'MMM dd, yyyy')
                          : 'Date unknown'}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
        {files.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No documents found
          </Typography>
        )}
      </Box>
    );
  };

  // Render by author view
  const renderAuthorView = () => {
    if (loadingAll) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading all documents from library...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    const files = filteredFiles;

    // Group by author
    const groups: Record<string, FileMetadata[]> = {};
    files.forEach(file => {
      const author = file.lastModifiedBy || 'Unknown';
      if (!groups[author]) {
        groups[author] = [];
      }
      groups[author].push(file);
    });

    return (
      <Box>
        {Object.keys(groups).sort().map(author => {
          // Sort files by name within each author group
          const sortedFiles = [...groups[author]].sort((a, b) => a.name.localeCompare(b.name));
          return (
            <Accordion key={author} defaultExpanded={true}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">
                  {author} ({groups[author].length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {sortedFiles.map((file) => (
                    <ListItem key={file.itemId} disablePadding>
                      <ListItemButton onClick={() => handleFileSelect(file)}>
                        <ListItemIcon>
                          <Description />
                        </ListItemIcon>
                        <ListItemText 
                          primary={file.name}
                          secondary={file.lastModifiedDateTime 
                            ? format(new Date(file.lastModifiedDateTime), 'MMM dd, yyyy')
                            : 'Date unknown'}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
        {files.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No documents found
          </Typography>
        )}
      </Box>
    );
  };

  const renderViewContent = () => {
    switch (viewMode) {
      case 'folder':
        return renderFolderView();
      case 'date':
        return renderDateView();
      case 'name':
        return renderNameView();
      case 'author':
        return renderAuthorView();
      default:
        return renderFolderView();
    }
  };

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        SharePoint Document Viewer
      </Typography>

      <Tabs
        value={viewMode}
        onChange={handleViewModeChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Folder" value="folder" />
        <Tab label="By Date" value="date" />
        <Tab label="By Name" value="name" />
        <Tab label="By Author" value="author" />
      </Tabs>

      {/* Search bar - show for date/name/author views */}
      {(viewMode === 'date' || viewMode === 'name' || viewMode === 'author') && (
        <TextField
          fullWidth
          placeholder="Search documents by name or author..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      )}

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {renderViewContent()}
      </Box>

      {/* Document Comments Panel */}
      {selectedDocument && (
        <DocumentComments 
          file={selectedDocument.isFile ? {
            spItemId: selectedDocument.spItemId ?? null,
            siteId: selectedDocument.siteId,
            driveId: selectedDocument.driveId,
            itemId: selectedDocument.itemId,
          } : null}
        />
      )}
    </Paper>
  );
};

export default SharePointDocumentViewer;

